'use client';

// 🏆 2026 Award-Winning Magical Effects Component 🏆
// Supports 100+ stunning effects with advanced animations

import { useEffect, useState } from 'react';
import { getEffectById, resolveEffectId } from '@/lib/magical-effects-catalog';
import { getEffect2026ById } from '@/lib/magical-effects-2026';

interface MagicalEffectProps {
  effectId: string;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  delay: number;
  size: number;
  color: string;
  rotation?: number;
  opacity?: number;
  duration?: number;
}

export default function MagicalEffect({ effectId }: MagicalEffectProps) {
  // Try 2026 catalog first, then fall back to legacy
  const resolvedId = resolveEffectId(effectId);
  const effect2026 = resolvedId ? getEffect2026ById(resolvedId) : null;
  const effectLegacy = resolvedId && !effect2026 ? getEffectById(resolvedId) : null;
  const effect = effect2026 || effectLegacy;
  
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    console.log(`[MagicalEffect] ✨ Effect: ${effectId}, Resolved: ${resolvedId}, Found: ${!!effect}, Type: ${effect2026 ? '2026' : 'Legacy'}, Animating: Always`);
    
    if (!effect) {
      console.error(`[MagicalEffect] ❌ Effect not found: "${effectId}" (even after fallback mapping)`);
      return;
    }

    // Generate particles based on density
    const densityMap = { low: 8, medium: 15, high: 25, ultra: 40 };
    const count = densityMap[effect.density];
    
    const newParticles: Particle[] = Array.from({ length: count }, (_, i) => {
      // Smart positioning based on animation type
      let startX = Math.random() * 100;
      let startY = -10 - Math.random() * 20;
      
      if (effect.animationType === 'rise') {
        startY = 100 + Math.random() * 20;
      } else if (effect.animationType === 'explode') {
        startX = 50 + (Math.random() - 0.5) * 20;
        startY = 50 + (Math.random() - 0.5) * 20;
      } else if (effect.animationType === 'orbit') {
        startX = 50;
        startY = 50;
      } else if (effect.animationType === 'spiral') {
        startX = 50 + Math.sin(i / count * Math.PI * 2) * 40;
        startY = -10 + (i / count) * 20;
      }
      
      return {
        id: i,
        x: startX,
        y: startY,
        delay: Math.random() * 6,
        size: 0.7 + Math.random() * 0.6, // 0.7-1.3x
        color: effect.colors[Math.floor(Math.random() * effect.colors.length)],
        rotation: Math.random() * 360,
        opacity: 0.7 + Math.random() * 0.3,
        duration: effect.speed === 'slow' ? 20 + Math.random() * 5 : 
                  effect.speed === 'medium' ? 15 + Math.random() * 3 :
                  10 + Math.random() * 2,
      };
    });
    
    setParticles(newParticles);
  }, [effectId, effect, effect2026, resolvedId]);

  if (!effect) return null;

  // Z-index based on layering
  const getZIndex = () => {
    if (effect.layering === 'front') return 50;
    if (effect.layering === 'behind') return 5;
    return 25; // mixed
  };

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: getZIndex(),
          overflow: 'hidden',
        }}
      >
        {particles.map((particle, index) => {
          // Mixed layering: alternate between front and back
          const isFront = effect.layering === 'mixed' ? index % 2 === 0 : effect.layering === 'front';
          const particleZ = isFront ? 50 : 5;
          
          // Check if effect has special properties (2026 only)
          const hasSpecial = 'special' in effect && effect.special;
          
          return (
            <div
              key={particle.id}
              style={{
                position: 'absolute',
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                fontSize: `${particle.size * 1.8}rem`,
                color: particle.color,
                opacity: particle.opacity || 0.85,
                zIndex: particleZ,
                animation: `${effect.animationType}Animation2026 ${particle.duration || '20s'} linear ${particle.delay}s infinite`,
                transform: hasSpecial && effect.special?.rotation ? `rotate(${particle.rotation}deg)` : undefined,
                textShadow: hasSpecial && effect.special?.glow
                  ? `0 0 10px ${particle.color}, 0 0 20px ${particle.color}, 0 0 30px rgba(255,255,255,0.5)`
                  : (effect.layering === 'front' || isFront)
                  ? '0 0 8px rgba(255,255,255,0.6)'
                  : 'none',
                filter: hasSpecial && effect.special?.blur ? 'blur(0.5px)' : undefined,
                willChange: 'transform, opacity',
              }}
            >
              {effect.icon}
              
              {/* Sparkle overlay for special effects */}
              {hasSpecial && effect.special?.sparkle && (
                <span style={{
                  position: 'absolute',
                  top: '-20%',
                  left: '-20%',
                  fontSize: '60%',
                  animation: 'sparkleAnimation2026 2s ease-in-out infinite',
                  opacity: 0.8,
                }}>
                  ✨
                </span>
              )}
              
              {/* Trail effect for special effects */}
              {hasSpecial && effect.special?.trail && (
                <>
                  <span style={{
                    position: 'absolute',
                    top: '10%',
                    left: '10%',
                    opacity: 0.5,
                    filter: 'blur(2px)',
                  }}>
                    {effect.icon}
                  </span>
                  <span style={{
                    position: 'absolute',
                    top: '20%',
                    left: '20%',
                    opacity: 0.3,
                    filter: 'blur(3px)',
                  }}>
                    {effect.icon}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>

      <style jsx>{`
        /* Enhanced 2026 Animations (backward compatible) */
        @keyframes fallAnimation2026 {
          from { transform: translateY(0) rotate(0deg); opacity: 0; }
          5% { opacity: 0.85; }
          95% { opacity: 0.85; }
          to { transform: translateY(120vh) rotate(720deg); opacity: 0; }
        }

        @keyframes floatAnimation2026 {
          0%, 100% { transform: translateY(0) translateX(0) scale(1); opacity: 0.85; }
          25% { transform: translateY(-25px) translateX(15px) scale(1.05); opacity: 1; }
          50% { transform: translateY(-15px) translateX(-15px) scale(0.95); opacity: 0.9; }
          75% { transform: translateY(-35px) translateX(10px) scale(1.02); opacity: 0.95; }
        }

        @keyframes pulseAnimation2026 {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.4); opacity: 1; filter: brightness(1.3); }
        }

        @keyframes spinAnimation2026 {
          0% { transform: rotate(0deg) scale(1); opacity: 0.8; }
          50% { transform: rotate(180deg) scale(1.3); opacity: 1; }
          100% { transform: rotate(360deg) scale(1); opacity: 0.8; }
        }

        @keyframes waveAnimation2026 {
          0% { transform: translateX(0) translateY(0) rotate(0deg); opacity: 0.85; }
          25% { transform: translateX(20px) translateY(-15px) rotate(10deg); opacity: 1; }
          50% { transform: translateX(0) translateY(0) rotate(0deg); opacity: 0.9; }
          75% { transform: translateX(-20px) translateY(15px) rotate(-10deg); opacity: 1; }
          100% { transform: translateX(0) translateY(0) rotate(0deg); opacity: 0.85; }
        }

        @keyframes riseAnimation2026 {
          from { transform: translateY(0) rotate(0deg) scale(0.8); opacity: 0; }
          5% { opacity: 0.85; }
          95% { opacity: 0.85; }
          to { transform: translateY(-120vh) rotate(-720deg) scale(1.2); opacity: 0; }
        }

        /* NEW 2026 Premium Animations */
        @keyframes spiralAnimation2026 {
          0% { transform: translateX(0) translateY(0) rotate(0deg); opacity: 0; }
          5% { opacity: 0.85; }
          25% { transform: translateX(30px) translateY(30vh) rotate(90deg); opacity: 1; }
          50% { transform: translateX(-30px) translateY(60vh) rotate(180deg); opacity: 0.9; }
          75% { transform: translateX(20px) translateY(90vh) rotate(270deg); opacity: 1; }
          100% { transform: translateX(0) translateY(120vh) rotate(360deg); opacity: 0; }
        }

        @keyframes explodeAnimation2026 {
          0% { transform: translate(0, 0) scale(0); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate(calc(var(--tx, 50vw) - 50%), calc(var(--ty, 50vh) - 50%)) scale(1.5); opacity: 0; }
        }

        @keyframes driftAnimation2026 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.7; }
          33% { transform: translate(15px, 10px) rotate(120deg); opacity: 0.9; }
          66% { transform: translate(-10px, 20px) rotate(240deg); opacity: 0.85; }
        }

        @keyframes bounceAnimation2026 {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.85; }
          10% { transform: translateY(20vh) scale(1.05); }
          20% { transform: translateY(0) scale(0.95); }
          30% { transform: translateY(10vh) scale(1.02); }
          40% { transform: translateY(0) scale(0.98); }
          50% { transform: translateY(5vh) scale(1); }
          60% { transform: translateY(0) scale(1); opacity: 0.9; }
        }

        @keyframes shimmer-waveAnimation2026 {
          0% { transform: translateX(-100%) translateY(0) scale(1); opacity: 0; }
          50% { transform: translateX(0) translateY(0) scale(1.2); opacity: 1; }
          100% { transform: translateX(100%) translateY(0) scale(1); opacity: 0; }
        }

        @keyframes twirlAnimation2026 {
          0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 0; }
          5% { opacity: 0.85; }
          25% { transform: translateY(30vh) rotate(180deg) scale(1.2); opacity: 1; }
          50% { transform: translateY(60vh) rotate(360deg) scale(0.9); opacity: 0.95; }
          75% { transform: translateY(90vh) rotate(540deg) scale(1.1); opacity: 1; }
          100% { transform: translateY(120vh) rotate(720deg) scale(1); opacity: 0; }
        }

        @keyframes cascadeAnimation2026 {
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
          5% { opacity: 0.9; }
          20% { transform: translateY(25vh) translateX(5px) rotate(90deg); opacity: 1; }
          40% { transform: translateY(50vh) translateX(-5px) rotate(180deg); opacity: 0.95; }
          60% { transform: translateY(75vh) translateX(3px) rotate(270deg); opacity: 1; }
          80% { transform: translateY(100vh) translateX(-3px) rotate(360deg); opacity: 0.9; }
          100% { transform: translateY(120vh) translateX(0) rotate(450deg); opacity: 0; }
        }

        @keyframes orbitAnimation2026 {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 0.7; }
          25% { transform: translate(40px, 0) rotate(90deg); opacity: 1; }
          50% { transform: translate(0, 40px) rotate(180deg); opacity: 0.85; }
          75% { transform: translate(-40px, 0) rotate(270deg); opacity: 1; }
          100% { transform: translate(0, 0) rotate(360deg); opacity: 0.7; }
        }

        @keyframes sparkleAnimation2026 {
          0%, 100% { transform: scale(0) rotate(0deg); opacity: 0; }
          50% { transform: scale(1.5) rotate(180deg); opacity: 1; }
        }
      `}</style>
    </>
  );
}

